import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";

import TestRailProjectsComponentPage from "../../../common-components/TestRailProjectsComponentPage.js";
import TestRailTemplatesComponentPage from "../../../common-components/TestRailTemplatesComponentPage.js";
import TestRailSuitesComponentPage from "../../../common-components/TestRailSuitesComponentPage.js";
import { LoadingModalContext, MessagePopupContext } from "../../ServicePage";
import {
  addTestCaseToTestRail,
  getAllTestRailProjects,
  getSectionsByProjectAndSuite,
  getTestSuitesByProjectID,
  getCaseTemplatesFromTestRail,
  getTestCaseFieldsFromTestRail,
} from "../../../../services/webServices";
import FieldMapper from "../../../common-components/FieldMapper.js";

const TestRailSelectionPage = ({
  taskType,
  issueType,
  setTestRailPageOpen,
  testCasesData,
}) => {
  const navigate = useNavigate();

  const { setMessagePopup, setMessage } = useContext(MessagePopupContext);
  const { setLoadingState } = useContext(LoadingModalContext);

  const [projectsList, setProjectsList] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [openTemplatePage, setOpenTemplatePage] = useState(false);

  const [suitesList, setSuitesList] = useState([]);
  const [selectedSuite, setSelectedSuite] = useState(null);
  const [openSuitePage, setOpenSuitePage] = useState(false);

  const [importLoading, setImportLoading] = useState(null);
  const [, setErrorMessage] = useState(null);

  const [testCases, setTestCases] = useState(testCasesData);
  const [templateFields, setTemplateFields] = useState([]);
  const [proceedingMap, setProceedingMap] = useState(null);

  const [fieldMappingCompleted, setFieldMappingCompleted] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState(null);

  const projectNameKey = `${selectedProject?.name}::${selectedTemplate?.id}`;

  // 1. Fetch all projects
  useEffect(() => {
    const fetchTestRailProjects = async () => {
      setImportLoading("Projects");
      try {
        const response = await getAllTestRailProjects();
        setProjectsList(response.data || []);
      } catch (error) {
        const errMsg =
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to fetch TestRail projects.";
        setMessage(errMsg);
        setMessagePopup(true);
      } finally {
        setImportLoading(null);
      }
    };

    fetchTestRailProjects();
  }, []);

  // 2. Fetch templates on project select
  useEffect(() => {
    if (!selectedProject) return;
    const fetchTemplates = async () => {
      setImportLoading("Templates");
      try {
        const response = await getCaseTemplatesFromTestRail(selectedProject.id);
        setTemplates(response.data || []);
        setOpenTemplatePage(true);
      } catch (err) {
        setMessage("Failed to fetch templates.");
        setMessagePopup(true);
      } finally {
        setImportLoading(null);
      }
    };
    fetchTemplates();
  }, [selectedProject]);

  // 3. Proceed when mapping is done
  // useEffect(() => {
  //   if (proceedingMap) {
  //     setSelectedMapping(proceedingMap);
  //     setFieldMappingCompleted(true);
  //   }
  // }, [proceedingMap]);

  // 4. Fetch suites after mapping
  useEffect(() => {
    if (!selectedTemplate) return;
    const fetchSuites = async () => {
      setImportLoading("Suites");
      setLoadingState({
        open: true,
        message: `Exporting ${issueType}`,
        progress: null,
      });

      try {
        const response = await getTestSuitesByProjectID(selectedProject.id);
        const suites = response.data || [];
        setSuitesList(suites);

        if (selectedProject.suite_mode > 1) {
          setOpenSuitePage(true);
        }
        if (selectedProject.suite_mode === 1 && suites.length > 0) {
          const firstSuite = suites[0];
          setSelectedSuite(firstSuite);
          await handleSuiteSelect(firstSuite);
        }
      } catch (err) {
        setMessage("Failed to fetch suites.");
        setMessagePopup(true);
      } finally {
        setImportLoading(null);
        setLoadingState({ open: false, message: "", progress: null });
      }
    };
    fetchSuites();
  }, [selectedTemplate]);

  const handleAvailableFields = async () => {
    setImportLoading("Fields");
    try {
      const response = await getTestCaseFieldsFromTestRail(
        selectedTemplate.id,
        selectedProject.id
      );
      const fields = response.data.fields || [];

      const systemFields = [
        { name: "title", label: "Title (System)" },
        { name: "priority_id", label: "Priority (System)" },
        { name: "type_id", label: "Type (System)" },
      ];
      const customFields = fields.filter((f) => !f.is_system);

      const allFields = [
        ...systemFields,
        ...customFields.map((field) => ({
          name: field.system_name,
          label: `${field.label || field.system_name} (Custom)`,
        })),
      ];

      setTemplateFields(allFields);
      return allFields;
    } catch (error) {
      const errMsg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to fetch fields for the selected template.";
      setMessage(errMsg);
      setMessagePopup(true);
    } finally {
      setImportLoading(null);
    }
  };

  const mapTestCasesWithSelectedMapping = () => {
    if (!selectedMapping?.mapping) return [];
    const priorityMap = { High: 3, Medium: 2, Low: 1 };
    return testCases.map((testCase) => {
      const mappedTestCase = { template_id: selectedTemplate?.id };
      const reverseMapping = {};

      Object.entries(selectedMapping.mapping).forEach(
        ([appField, testRailFieldObj]) => {
          const testRailField =
            typeof testRailFieldObj === "object" && !!testRailFieldObj
              ? testRailFieldObj.name
              : testRailFieldObj;
          if (!testRailField) return;
          if (!reverseMapping[testRailField])
            reverseMapping[testRailField] = [];
          reverseMapping[testRailField].push(appField);
        }
      );

      Object.entries(reverseMapping).forEach(([testRailField, appFields]) => {
        if (testRailField === "type_id") return;
        const values = appFields
          .map((f) => testCase[f])
          .filter((v) => v !== undefined && v !== null && v !== "");

        if (!values.length) return;

        if (testRailField === "priority_id") {
          mappedTestCase[testRailField] =
            priorityMap[values[0]] || priorityMap["Medium"];
        } else if (
          ["custom_steps_separated", "custom_testrail_bdd_scenario"].includes(
            testRailField
          )
        ) {
          mappedTestCase[testRailField] = values
            .flatMap((v) =>
              Array.isArray(v)
                ? v
                : String(v)
                    .split(/\n+/)
                    .map((s) => s.trim())
            )
            .filter(Boolean)
            .map((step) => ({ content: step, expected: "" }));
        } else {
          mappedTestCase[testRailField] = values.join("\n");
        }
      });

      return mappedTestCase;
    });
  };

  const handleSuiteSelect = async (suite) => {
    setSelectedSuite(suite);
    setLoadingState({
      open: true,
      message: `Exporting ${issueType}`,
      progress: null,
    });

    try {
      const sectionResponse = await getSectionsByProjectAndSuite(
        selectedProject.id,
        suite.id
      );
      const sections = sectionResponse.data;
      if (!sections?.length) {
        setMessage("No sections found to add test cases.");
        setMessagePopup(true);
        setLoadingState({ open: false, message: "", progress: null });
        return;
      }

      // Now show loader for exporting cases
      setLoadingState({
        open: true,
        message: `Exporting ${issueType}`,
        progress: null,
      });

      const mappedTestCases = mapTestCasesWithSelectedMapping();
      const payload = {
        projectName : `${selectedProject.name}->${selectedTemplate.name}`, 
        templateId : selectedTemplate.id,
        section_id: sections[0].id,
        testCases: testCases,
        
      };

      const addRes = await addTestCaseToTestRail(payload);

      setLoadingState({ open: false, message: "", progress: null });

      if (addRes.data.totalFailed === payload.testCases.length) {
        setMessage(
          "All cases were rejected due to invalid mappings. Please update and try again."
        );
      } else if (addRes.data.totalFailed > 0) {
        setMessage(
          `${addRes.data.totalFailed} test cases failed to export, rest added successfully.`
        );
      } else {
        setMessage("All test cases added successfully.");
      }
      setMessagePopup(true);
      setTestRailPageOpen(false);
    } catch (error) {
      console.error(error);
      setLoadingState({ open: false, message: "", progress: null });

      setMessage("Failed to add test cases to TestRail.");
      setMessagePopup(true);
      setTestRailPageOpen(false);
    }
  };

  return (
    <>
      {!selectedProject && (
        <TestRailProjectsComponentPage
          taskType="Export"
          issueType={issueType}
          projectsLoading={importLoading === "Projects"}
          setOpenProjectPage={setTestRailPageOpen}
          projectsList={projectsList}
          selectedProject={selectedProject}
          setSelectedProject={setSelectedProject}
          setOpenTemplatePage={setOpenTemplatePage}
          setSelectedTemplate={setSelectedTemplate}
          setTemplates={setTemplates}
          setSelectedSuite={setSelectedSuite}
          setSuitesList={setSuitesList}
        />
      )}

      {selectedProject && !selectedTemplate && (
        <TestRailTemplatesComponentPage
          selectedProject={selectedProject}
          setTemplates={setTemplates}
          templates={templates}
          templatesLoading={importLoading === "Templates"}
          selectedTemplate={selectedTemplate}
          setSelectedTemplate={setSelectedTemplate}
          setOpenTemplatePage={setOpenTemplatePage}
          setOpenSuitePage={setOpenSuitePage}
          setSelectedProject={setSelectedProject}
          setALMModalOpen={setTestRailPageOpen}
        />
      )}

      {/* {selectedTemplate && !proceedingMap && (
        <FieldMapper
          taskType="Export"
          setProceedingMap={setProceedingMap}
          setALMModalOpen={setTestRailPageOpen}
          setPreviousSelection={setSelectedTemplate}
          toolName={"Test Rail"}
          projectName={projectNameKey}
          issueType={issueType}
          getAvailableFields={handleAvailableFields}
          setOpenTemplatePage={setOpenTemplatePage}
        />
      )} */}

      {openSuitePage && suitesList.length > 1 && (
        <TestRailSuitesComponentPage
          selectedProject={selectedProject}
          suitesList={suitesList}
          suitesLoading={importLoading === "Suites"}
          selectedSuite={selectedSuite}
          setSelectedSuite={setSelectedSuite}
          setOpenSuitePage={setOpenSuitePage}
          onSuiteSelect={handleSuiteSelect}
          setSuitesList={setSuitesList}
          setALMModalOpen={setTestRailPageOpen}
          setProceedingMap={setProceedingMap}
        />
      )}
    </>
  );
};

export default TestRailSelectionPage;
